/**
 * Every email a work order sent its customer (#3787, #4187): its own (the
 * "vehicle is ready" email and replies in a mailbox thread attached to it) and
 * those of its quotes, additional-work offers, inspection reports, invoices and
 * receipts, newest first, each named by its document. The server leaves out
 * the documents the viewer may not read. No rows means nothing was emailed
 * (or not since delivery tracking began), not that delivery is in doubt.
 *
 * Closed by default behind a button whose colour, icon and text give the
 * overall state: red when the newest email of some document failed, yellow
 * while one is still on its way, green when all arrived. The button and the
 * list share one query (cache key), so the button costs no request of its own.
 */
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Collapse, type ButtonProps } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { EmailDeliveryHistory } from '@/components/email/EmailDeliveryHistory';
import { useOutboundEmails } from '@/hooks/useOutboundEmails';
import { overallEmailState, type OverallEmailState } from '@/components/email/email-delivery-status';

const OVERALL: Record<OverallEmailState, { color: ButtonProps['color']; icon: ReactNode; labelKey: string }> = {
  failed: { color: 'error', icon: <ErrorOutlineIcon />, labelKey: 'emailDelivery.overall.failed' },
  pending: { color: 'warning', icon: <ScheduleIcon />, labelKey: 'emailDelivery.overall.pending' },
  delivered: { color: 'success', icon: <CheckCircleOutlineIcon />, labelKey: 'emailDelivery.overall.delivered' },
  none: { color: 'inherit', icon: <MailOutlineIcon />, labelKey: 'emailDelivery.overall.none' },
};

export function WorkOrderEmailDeliveries({ workOrderId }: { workOrderId: string }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const query = useOutboundEmails('booking', workOrderId);
  // Until the summary is read the button claims no state.
  const overall = query.isSuccess ? overallEmailState(query.data.summary) : null;
  const style = OVERALL[overall ?? 'none'];

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        color={style.color}
        startIcon={style.icon}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid="work-order-email-toggle"
        data-overall={overall ?? 'loading'}
        onClick={() => { setOpen((value) => !value); }}
        sx={{ my: 1 }}
      >
        {t('emailDelivery.workOrderToggle')}
        {overall ? ` · ${t(style.labelKey)}` : ''}
      </Button>
      <Collapse in={open} id={panelId} unmountOnExit>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <EmailDeliveryHistory
              entityType="booking"
              entityId={workOrderId}
              title={t('emailDelivery.workOrderTitle')}
              emptyText={t('emailDelivery.workOrderEmpty')}
              showDocument
            />
          </CardContent>
        </Card>
      </Collapse>
    </>
  );
}
